import React from 'react';
import { Link, useForm } from '@inertiajs/react';
import MainLayout from '../../layouts/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { FiArrowLeft, FiCalendar, FiUser, FiHome, FiMessageSquare, FiPhone, FiMail } from 'react-icons/fi';

export default function LeadShow({ lead, conversations = [], user }) {
  const { data, setData, patch, processing, errors } = useForm({
    status: lead.status || 'new',
    source: lead.source || '',
    toured_at: lead.toured_at || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    patch(`/facility_owner/leads/${lead.id}`);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };

  return (
    <MainLayout user={user}>
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <Button variant="outline" className="mb-4" asChild>
              <Link href="/facility_owner/leads">
                <FiArrowLeft className="mr-2 h-4 w-4" />
                Back to Leads
              </Link>
            </Button>
            
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Lead Details</h1>
                <p className="text-gray-500">
                  {lead.user ? lead.user.email : 'Anonymous Lead'} · Created {formatDate(lead.created_at)}
                </p>
              </div>
              <Badge 
                className="mt-2 md:mt-0" 
                variant={
                  lead.status === 'new' ? 'secondary' :
                  lead.status === 'contacted' ? 'default' :
                  lead.status === 'qualified' ? 'success' :
                  lead.status === 'converted' ? 'success' :
                  'destructive'
                }
              >
                {lead.status.charAt(0).toUpperCase() + lead.status.slice(1)}
              </Badge>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>Lead Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Facility</h3>
                    <p className="mt-1 flex items-center">
                      <FiHome className="mr-2 h-4 w-4 text-gray-400" />
                      {lead.facility?.name || 'Unknown facility'}
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Contact</h3>
                    <p className="mt-1 flex items-center">
                      <FiUser className="mr-2 h-4 w-4 text-gray-400" />
                      {lead.user?.email || 'Anonymous'}
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Source</h3>
                    <p className="mt-1 flex items-center">
                      <FiCalendar className="mr-2 h-4 w-4 text-gray-400" />
                      {lead.source || 'Not specified'}
                    </p>
                  </div>
                  
                  {lead.toured_at && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Tour Date</h3>
                      <p className="mt-1 flex items-center">
                        <FiCalendar className="mr-2 h-4 w-4 text-gray-400" />
                        {formatDate(lead.toured_at)}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {conversations && conversations.length > 0 && (
                <Card className="mt-6">
                  <CardHeader>
                    <CardTitle>Conversations</CardTitle>
                    <CardDescription>Messages exchanged with this lead</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      {conversations.map((conversation) => (
                        <div key={conversation.id} className="border rounded-lg p-4">
                          <div className="flex justify-between items-center mb-3">
                            <h3 className="font-medium">{formatDate(conversation.created_at)}</h3>
                            <Badge>{conversation.messages.length} messages</Badge>
                          </div>
                          <div className="space-y-3">
                            {conversation.messages.map((message, index) => (
                              <div 
                                key={index} 
                                className={`p-3 rounded-lg ${
                                  message.user_id === user.id 
                                    ? 'bg-blue-50 ml-6' 
                                    : 'bg-gray-50 mr-6'
                                }`}
                              >
                                <div className="flex justify-between items-center mb-1">
                                  <span className="text-xs text-gray-500">
                                    {message.user_id === user.id ? 'You' : 'Lead'}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {new Date(message.created_at).toLocaleTimeString()}
                                  </span>
                                </div>
                                <p>{message.content}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
              
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle>Update Lead Status</CardTitle>
                  <CardDescription>Change the status of this lead</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="status">Status</Label>
                      <Select
                        value={data.status}
                        onValueChange={(value) => setData('status', value)}
                      >
                        <SelectTrigger className={errors.status ? 'border-red-500' : ''}>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="qualified">Qualified</SelectItem>
                          <SelectItem value="converted">Converted</SelectItem>
                          <SelectItem value="lost">Lost</SelectItem>
                        </SelectContent>
                      </Select>
                      {errors.status && <p className="text-red-500 text-sm mt-1">{errors.status}</p>}
                    </div>
                    
                    <div>
                      <Label htmlFor="source">Source</Label>
                      <Select
                        value={data.source || ''}
                        onValueChange={(value) => setData('source', value)}
                      >
                        <SelectTrigger className={errors.source ? 'border-red-500' : ''}>
                          <SelectValue placeholder="Select source" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="website">Website</SelectItem>
                          <SelectItem value="referral">Referral</SelectItem>
                          <SelectItem value="phone">Phone</SelectItem>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="social_media">Social Media</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      {errors.source && <p className="text-red-500 text-sm mt-1">{errors.source}</p>}
                    </div>
                    
                    <div>
                      <Label htmlFor="toured_at">Tour Date</Label>
                      <input
                        id="toured_at"
                        type="date"
                        value={data.toured_at ? new Date(data.toured_at).toISOString().split('T')[0] : ''}
                        onChange={e => setData('toured_at', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md ${errors.toured_at ? 'border-red-500' : 'border-gray-300'}`}
                      />
                      {errors.toured_at && <p className="text-red-500 text-sm mt-1">{errors.toured_at}</p>}
                    </div>
                    
                    <div className="flex justify-end pt-2">
                      <Button type="submit" disabled={processing}>
                        Update Lead
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
            
            <div>
              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button className="w-full justify-start" variant="outline" asChild>
                    <Link href={`/facility_owner/facilities/${lead.facility?.id}`}>
                      <FiHome className="mr-2 h-4 w-4" />
                      View Facility
                    </Link>
                  </Button>
                  
                  {lead.user && (
                    <>
                      <Button className="w-full justify-start" variant="outline">
                        <FiMail className="mr-2 h-4 w-4" />
                        Send Email
                      </Button>
                      
                      <Button className="w-full justify-start" variant="outline">
                        <FiPhone className="mr-2 h-4 w-4" />
                        Call Lead
                      </Button>
                    </>
                  )}
                  
                  <Button className="w-full justify-start" variant="outline">
                    <FiMessageSquare className="mr-2 h-4 w-4" />
                    Send Message
                  </Button>
                </CardContent>
              </Card>
              
              {lead.status === 'new' && (
                <div className="mt-6">
                  <Button 
                    className="w-full" 
                    variant="default"
                    onClick={() => {
                      setData('status', 'contacted');
                      patch(`/facility_owner/leads/${lead.id}`);
                    }}
                  >
                    Mark as Contacted
                  </Button>
                </div>
              )}
              
              {lead.status === 'contacted' && (
                <div className="mt-6">
                  <Button 
                    className="w-full" 
                    variant="success"
                    onClick={() => {
                      setData('status', 'qualified');
                      patch(`/facility_owner/leads/${lead.id}`);
                    }}
                  >
                    Mark as Qualified
                  </Button>
                </div>
              )}
              
              {(lead.status === 'qualified' || lead.status === 'contacted') && (
                <div className="mt-3">
                  <Button 
                    className="w-full" 
                    variant="success"
                    onClick={() => {
                      setData('status', 'converted');
                      patch(`/facility_owner/leads/${lead.id}`);
                    }}
                  >
                    Mark as Converted
                  </Button>
                </div>
              )}
              
              {lead.status !== 'lost' && (
                <div className="mt-3">
                  <Button 
                    className="w-full" 
                    variant="destructive"
                    onClick={() => {
                      setData('status', 'lost');
                      patch(`/facility_owner/leads/${lead.id}`);
                    }}
                  >
                    Mark as Lost
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
